import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { isoDate } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import ScanDocumentBox from "@/components/ScanDocumentBox";
import Combobox from "@/components/Combobox";
import { MARQUES, getModelesForMarque } from "@/lib/marques";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Car, Wallet, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import Plate from "@/components/Plate";
import StatusBadge from "@/components/StatusBadge";

const emptyVente = {
  client_nom: "", marque: "", modele: "", annee: "", plaque: "",
  numero_vo: "",
  prix_vente: "",
  date_vente: isoDate(),
  date_depart: "",
  carte_grise_disponible: false,
  notes: "",
};

export function QuickVenteDialog({ onCreated }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyVente);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        annee: form.annee ? Number(form.annee) : null,
        kilometrage: 0,
        prix_vente: Number(form.prix_vente || 0),
        carte_grise_disponible: !!form.carte_grise_disponible,
      };
      const { data } = await api.post("/ventes", payload);
      toast.success("Vente créée");
      setOpen(false);
      setForm(emptyVente);
      onCreated?.();
      navigate(`/ventes/${data.id}`);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Erreur lors de la création");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="quick-vente-btn" className="bg-klein hover:bg-blue-800 text-white rounded-sm">
          <Car size={14} className="mr-1" /> Nouvelle vente
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Nouvelle vente</DialogTitle>
        </DialogHeader>

        <ScanDocumentBox
          onExtract={(data) => {
            setForm((f) => ({
              ...f,
              marque: data.marque || f.marque,
              modele: data.modele || f.modele,
              annee: data.annee || f.annee,
              plaque: (data.plaque || f.plaque || "").replace(/-/g, "").toUpperCase(),
              vin: data.vin || f.vin,
              couleur: data.couleur || f.couleur,
              kilometrage: data.kilometrage ?? f.kilometrage,
            }));
          }}
        />

        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Client *</Label>
              <Input data-testid="qv-client" required value={form.client_nom} onChange={(e) => setForm({ ...form, client_nom: e.target.value })} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Date vente</Label>
              <Input data-testid="qv-date" type="date" value={form.date_vente} onChange={(e) => setForm({ ...form, date_vente: e.target.value })} className="mt-1 rounded-sm" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Marque *</Label>
              <Combobox
                testId="qv-marque"
                value={form.marque}
                onChange={(v) => setForm({ ...form, marque: v, modele: "" })}
                options={MARQUES}
                placeholder="Tapez ou choisissez…"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Modèle *</Label>
              <Combobox
                testId="qv-modele"
                value={form.modele}
                onChange={(v) => setForm({ ...form, modele: v })}
                options={getModelesForMarque(form.marque)}
                placeholder={form.marque ? "Tapez ou choisissez…" : "Sélectionnez la marque"}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Année</Label>
              <Input data-testid="qv-annee" type="number" inputMode="decimal" value={form.annee} onChange={(e) => setForm({ ...form, annee: e.target.value })} className="mt-1 rounded-sm" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Plaque *</Label>
              <Input data-testid="qv-plaque" required value={form.plaque} onChange={(e) => setForm({ ...form, plaque: e.target.value.toUpperCase() })} className="mt-1 rounded-sm font-mono uppercase tracking-widest" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">N° VO</Label>
              <Input data-testid="qv-numero-vo" value={form.numero_vo} onChange={(e) => setForm({ ...form, numero_vo: e.target.value })} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Prix de vente *</Label>
              <Input data-testid="qv-prix" type="number" inputMode="decimal" step="0.01" required value={form.prix_vente} onChange={(e) => setForm({ ...form, prix_vente: e.target.value })} className="mt-1 rounded-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Date de départ</Label>
              <Input data-testid="qv-date-depart" type="date" value={form.date_depart} onChange={(e) => setForm({ ...form, date_depart: e.target.value })} className="mt-1 rounded-sm" />
            </div>
            <label className="inline-flex items-center gap-2 px-3 py-2 border border-border rounded-sm cursor-pointer select-none mt-6 h-10">
              <input
                type="checkbox"
                data-testid="qv-cartegrise"
                checked={!!form.carte_grise_disponible}
                onChange={(e) => setForm({ ...form, carte_grise_disponible: e.target.checked })}
                className="w-4 h-4 accent-klein"
              />
              <span className="text-sm">Carte grise disponible</span>
            </label>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="qv-submit" className="bg-klein hover:bg-blue-800 text-white rounded-sm">
              {saving ? "Création…" : "Créer la vente"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const emptyPay = {
  mode: "vente",
  vente_id: "",
  client_nom: "",
  montant: "",
  methode: "Espèces",
  date_paiement: isoDate(),
  notes: "",
};

export function QuickPaiementDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyPay);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selectedVente, setSelectedVente] = useState(null);
  const [saving, setSaving] = useState(false);

  const runSearch = async (q) => {
    if (!q || !q.trim()) {
      setResults([]);
      return;
    }
    const { data } = await api.get("/ventes/search/quick", { params: { q } });
    setResults(data);
  };

  // No initial preload: user must type something to see suggestions.

  const submit = async (e) => {
    e.preventDefault();
    if (form.mode === "vente" && !form.vente_id) {
      toast.error("Sélectionnez une vente, ou passez en mode 'Paiement libre'");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        vente_id: form.mode === "libre" ? null : form.vente_id,
        client_nom: form.mode === "libre" ? form.client_nom : (selectedVente?.client_nom || ""),
        montant: Number(form.montant || 0),
        methode: form.methode,
        date_paiement: form.date_paiement,
        notes: form.notes,
      };
      await api.post("/paiements", payload);
      toast.success(payload.vente_id ? "Paiement lié à la vente" : "Acompte libre enregistré");
      setOpen(false);
      setForm(emptyPay);
      setSelectedVente(null);
      setQuery("");
      onCreated?.();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="quick-paiement-btn" variant="outline" className="rounded-sm">
          <Wallet size={14} className="mr-1" /> Nouveau paiement
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Nouveau paiement</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="flex gap-2 p-1 bg-slate-100 rounded-sm">
            <button type="button" onClick={() => setForm({ ...form, mode: "vente" })} className={`flex-1 px-3 py-1.5 rounded-sm text-sm font-medium ${form.mode === "vente" ? "bg-white shadow-sm" : "text-muted-foreground"}`} data-testid="qp-mode-vente">
              Lié à une vente
            </button>
            <button type="button" onClick={() => setForm({ ...form, mode: "libre" })} className={`flex-1 px-3 py-1.5 rounded-sm text-sm font-medium ${form.mode === "libre" ? "bg-white shadow-sm" : "text-muted-foreground"}`} data-testid="qp-mode-libre">
              Paiement libre
            </button>
          </div>

          {form.mode === "vente" ? (
            <div>
              <Label className="text-xs uppercase tracking-wider">Rechercher (plaque ou client) *</Label>
              <div className="flex items-center gap-2 mt-1">
                <div className="relative flex-1">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    data-testid="qp-search"
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); runSearch(e.target.value); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runSearch(query); } }}
                    placeholder="Plaque ou nom"
                    className="pl-9 rounded-sm"
                  />
                </div>
                <Button type="button" variant="outline" onClick={() => runSearch(query)} className="rounded-sm">Chercher</Button>
              </div>
              {selectedVente && (
                <div className="mt-3 p-3 border border-klein/30 bg-blue-50/40 rounded-sm flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Plate value={selectedVente.plaque} small />
                    <div className="text-sm">
                      <div className="font-medium">{selectedVente.marque} {selectedVente.modele} — {selectedVente.client_nom}</div>
                    </div>
                  </div>
                  <StatusBadge statut={selectedVente.statut} />
                </div>
              )}
              {!selectedVente && results.length > 0 && (
                <div className="mt-2 max-h-44 overflow-y-auto border border-border rounded-sm divide-y">
                  {results.slice(0, 8).map((v) => (
                    <button key={v.id} type="button" onClick={() => { setSelectedVente(v); setForm({ ...form, vente_id: v.id }); }} className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between" data-testid={`qp-result-${v.id}`}>
                      <div className="flex items-center gap-2"><Plate value={v.plaque} small /><span className="text-sm">{v.marque} {v.modele} — {v.client_nom}</span></div>
                      <StatusBadge statut={v.statut} />
                    </button>
                  ))}
                </div>
              )}
              {!selectedVente && results.length === 0 && query.trim() && (
                <div className="mt-2 text-xs text-muted-foreground px-1">
                  Aucun véhicule trouvé pour « {query} ».
                </div>
              )}
              {!selectedVente && results.length === 0 && !query.trim() && (
                <div className="mt-2 text-xs text-muted-foreground px-1">
                  Tapez une plaque, un nom de client, une marque ou un modèle pour trouver la vente.
                </div>
              )}
            </div>
          ) : (
            <div className="p-3 border border-amber-200 bg-amber-50/40 rounded-sm">
              <Label className="text-xs uppercase tracking-wider">Nom du client (facultatif)</Label>
              <Input data-testid="qp-libre-client" value={form.client_nom} onChange={(e) => setForm({ ...form, client_nom: e.target.value })} className="mt-1 rounded-sm bg-white" />
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Montant *</Label>
              <Input data-testid="qp-montant" type="number" inputMode="decimal" step="0.01" required value={form.montant} onChange={(e) => setForm({ ...form, montant: e.target.value })} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Méthode</Label>
              <Select value={form.methode} onValueChange={(v) => setForm({ ...form, methode: v })}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Espèces">Espèces</SelectItem>
                  <SelectItem value="Virement">Virement</SelectItem>
                  <SelectItem value="Chèque">Chèque</SelectItem>
                  <SelectItem value="Carte">Carte</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Date</Label>
              <Input data-testid="qp-date" type="date" value={form.date_paiement} onChange={(e) => setForm({ ...form, date_paiement: e.target.value })} className="mt-1 rounded-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="qp-submit" className="bg-klein hover:bg-blue-800 text-white rounded-sm">
              {saving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
